import {pillowLayout} from './pillow-layout'
import * as T from 'three'
import type {BeddingId,BeddingSelection} from '../../../../../config/blissBedding'
import {createPillow} from './soft-bedding'

const palettes={
 hotel_white:{main:'#faf7ef',pattern:'#e8e1d3',accent:'#eee6d8',trim:'#c6b99d'},
 pearl_atelier:{main:'#efe5d2',pattern:'#c9b58c',accent:'#fff9eb',trim:'#b1955b'},
 riviera_bleu:{main:'#8eafc3',pattern:'#e2e9ec',accent:'#18384f',trim:'#c1c9cd'},
 emerald_nocturne:{main:'#164e3b',pattern:'#275e48',accent:'#e4d2aa',trim:'#b29a60'},
}
/** Owned texture per material: no shared GPU texture survives a scene rebuild. */
function textile(base:string,accent:string,ornate=false){
 const size=256,data=new Uint8Array(size*size*4),rgb=(s:string)=>[1,3,5].map(i=>parseInt(s.slice(i,i+2),16)),a=rgb(base),b=rgb(accent)
 for(let y=0;y<size;y++)for(let x=0;x<size;x++){
  const u=x/size*Math.PI*2,v=y/size*Math.PI*2
  // Woven threads plus a repeating leaf-and-scroll jacquard impression.
  const leaf=Math.pow(Math.max(0,Math.cos(u*2+Math.sin(v)*1.4)*Math.cos(v*2)),8)
  const thread=(x%4<2)!==(y%4<2)?1:.94,blend=ornate?leaf*.7:0,i=(y*size+x)*4
  for(let c=0;c<3;c++)data[i+c]=Math.round((a[c]*(1-blend)+b[c]*blend)*thread)
  data[i+3]=255
 }
 const t=new T.DataTexture(data,size,size,T.RGBAFormat);t.colorSpace=T.SRGBColorSpace;t.wrapS=t.wrapT=T.RepeatWrapping;t.repeat.set(3,5);t.generateMipmaps=true;t.minFilter=T.LinearMipmapLinearFilter;t.anisotropy=8;t.needsUpdate=true;return t
}
function cloth(color:string,pattern:string,ornate=false){
 const map=textile(color,pattern,ornate)
 return new T.MeshPhongMaterial({color:0xffffff,map,bumpMap:map,bumpScale:.008,shininess:8,specular:0x171717,side:T.DoubleSide})
}
/** Local inches: X across the mattress, Z from pillow end to foot; Y=0 at mattress top. */
export function createBeddingSet(width:number,length:number,id:BeddingId,upper=false,options?:BeddingSelection){
 const root=new T.Group();root.name=`bedding-${id}`;root.userData.bedding=true
 if(id==='original'||id==='bare')return root
 const p=palettes[id],luxury=id!=='hotel_white',ornate=id==='pearl_atelier'||id==='riviera_bleu'
 function quilt(name:string,w:number,d:number,z:number,height:number,color:string,pattern:string,patterned=false){
  const geo=new T.PlaneGeometry(w/12,d/12,48,64);geo.rotateX(-Math.PI/2)
  const pos=geo.getAttribute('position')
  for(let i=0;i<pos.count;i++){
   const x=pos.getX(i)*12,v=pos.getZ(i)*12
   const edge=Math.min(1,Math.max(0,(w/2-Math.abs(x))/2.5),Math.max(0,(d/2-Math.abs(v))/2.5))
   const seamX=Math.pow(Math.abs(Math.cos((x+w/2)/6*Math.PI)),18),seamZ=Math.pow(Math.abs(Math.cos((v+d/2)/8*Math.PI)),18)
   const ripple=.08*Math.sin(x*.5+v*.3)
   pos.setY(i,(height+edge*(.55-(seamX+seamZ)*.15)+ripple)/12)
  }
  geo.computeVertexNormals();const mesh=new T.Mesh(geo,cloth(color,pattern,patterned));mesh.name=name;mesh.position.z=z/12;mesh.castShadow=true;mesh.receiveShadow=true;root.add(mesh)
  const pts=Array.from({length:129},(_,i)=>{
   const side=Math.floor(i/32)%4,t=(i%32)/32
   const x=side===0?-w/2+w*t:side===1?w/2:side===2?w/2-w*t:-w/2
   const dz=side===0?-d/2:side===1?-d/2+d*t:side===2?d/2:d/2-d*t
   return new T.Vector3(x/12,(height+.05)/12,(z+dz)/12)
  });const hem=new T.Line(new T.BufferGeometry().setFromPoints(pts),new T.LineBasicMaterial({color:p.trim}));hem.name=`${name}-piping`;root.add(hem)
 }
 // All layers remain within the mattress rectangle and stop before the foot edge.
 const start=upper?20:24,end=length-1.25
 quilt('quilted-comforter',width-1,end-start,(start+end)/2,upper?.28:.45,p.main,p.pattern,ornate)
 quilt('comforter-foldback',width-1.5,8,start+4,upper?1.05:1.35,id==='emerald_nocturne'?p.accent:'#f9f4e9',p.pattern)
 if(luxury)quilt('woven-foot-coverlet',width-2,13,length-10,upper?1.05:1.3,id==='riviera_bleu'?'#eee8db':p.accent,p.trim)
 function pillow(name:string,x:number,z:number,w:number,d:number,h:number,color:string,tilt=0,patterned=false){
  const key=`${upper?'upper':'lower'}:${name}`,widthChoice=options?.pillowWidths?.[key]??1,styleChoice=options?.pillowStyles?.[key]??0;
  w=Math.min(w*[.85,1,1.12][widthChoice%3],Math.max(6,width-2*Math.abs(x)-2),name.startsWith('sleeping-pillow')&&count===2?pw+1:Infinity);
  if(!name.startsWith('sleeping-pillow')){const mainIndex=name.startsWith('luxury-sham')?Number(name.split('-').at(-1)):0;const mainChoice=options?.pillowWidths?.[`${upper?'upper':'lower'}:sleeping-pillow-${mainIndex}`]??1;w=Math.min(w,pw*[.85,1,1.12][mainChoice%3]*.8);}
  if(name==='embroidered-lumbar'){
   const mainChoice=options?.pillowWidths?.[`${upper?'upper':'lower'}:sleeping-pillow-0`]??1;
   const shamChoice=options?.pillowWidths?.[`${upper?'upper':'lower'}:luxury-sham-0`]??1;
   const shamWidth=Math.min(pw*.8*[.85,1,1.12][shamChoice%3],pw*[.85,1,1.12][mainChoice%3]*.8);
   w=Math.min(w,shamWidth*.8);
  }
  color=styleChoice===1?'#fffaf1':styleChoice===2?'#29495d':styleChoice===3?'#879679':color;
  patterned=styleChoice===3||patterned;
  const mesh=createPillow(w,d,h);mesh.userData.pillowKey=key;const original=mesh.material as T.MeshLambertMaterial;original.map?.dispose();original.dispose();mesh.material=cloth(color,p.trim,patterned)
  mesh.rotation.x=tilt;mesh.position.set(x/12,(Math.abs(Math.sin(tilt))*d/2+Math.cos(tilt)*h/2+.25)/12,z/12);mesh.name=name
  // Ground the actual curved pillow, not the larger imaginary box around it.
  // Upper pillows form a compact stack against the headboard.
  if(upper){
   mesh.updateMatrixWorld(true)
   const bounds=new T.Box3().setFromObject(mesh)
   mesh.position.y+=.04/12-bounds.min.y
   const back=name.startsWith('sleeping-pillow')?.5:name.startsWith('luxury-sham')?5:9
   mesh.position.z+=back/12-bounds.min.z
  }
  mesh.children.forEach(c=>{if(c instanceof T.Line)(c.material as T.LineBasicMaterial).color.set(p.trim)});root.add(mesh)
 }
 const {count,width:pw,depth:pd,offsets}=pillowLayout(width)
 for(let i=0;i<count;i++){
  const x=offsets[i]
  pillow(`sleeping-pillow-${i}`,x,luxury?8:pd/2+.5,pw,pd,5,'#f9f5ec',luxury?1.05:0)
  if(luxury)pillow(`luxury-sham-${i}`,x,14,pw*.8,12,4.5,id==='pearl_atelier'?p.main:p.accent,.95,ornate)
 }
 if(luxury)pillow('embroidered-lumbar',0,19,Math.min(pw*.7,22),8,4.5,id==='riviera_bleu'?'#eee8dc':p.accent,.8,true)
 return root
}

